import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { format } from 'date-fns'
import { Container, Breadcrumb, BreadcrumbSection, BreadcrumbDivider, Form, FormGroup, FormField, Input, TextArea, Button, Icon, Message, Checkbox, Pagination, TableRow, TableCell, Popup, TableHeaderCell, TableBody, Table, TableHeader, Divider, Header } from 'semantic-ui-react'

const Reseller = ({ reseller, goBack }) => {
    const [itemsPerPage] = useState(10)
    const [activePage, setActivePage] = useState(0)
    const [name, setName] = useState(reseller.name)
    const [phone, setPhone] = useState(reseller.phone)
    const [email, setEmail] = useState(reseller.email)
    const [password, setPassword] = useState(reseller.password)
    const [balance, setBalance] = useState(reseller.balance?.toFixed(2))
    const [markup, setMarkup] = useState(reseller.markup)
    const [remarks, setRemarks] = useState(reseller.remarks)
    const [disableDiscounts, setDisableDiscounts] = useState(reseller.disableDiscounts)
    const [showSuccessMessage, setShowSuccessMessage] = useState(false)
    const [launchedGames, setLaunchedGames] = useState([])

    useEffect(() => { // determine the mark up fields in renderMarkupFields() with launchedGames data
        axios.get('/api/launched_games').then((res) => setLaunchedGames(res.data))
    }, [])

    const handleChangeField = e => {
        const { name, value } = e.target
        if (name === 'name') setName(value)
        if (name === 'phone') setPhone(value)
        if (name === 'email') setEmail(value)
        if (name === 'password') setPassword(value)
        if (name === 'balance') setBalance(value)
        if (name.includes('markup')) {
            const key = name.split('-')[1]
            const newMarkup = {...markup}
            newMarkup[key] = value
            setMarkup(newMarkup)
        }
        if (name === 'remarks') setRemarks(value)
        setShowSuccessMessage(false)
    }
    const handleToggle = (e, data) => {
        setDisableDiscounts(data.checked)
        setShowSuccessMessage(false)
    }
    const handlePageChange = (e, { activePage }) => {
        setActivePage(activePage - 1)
    }
    const handleSaveClicked = async () => {
        const convertedMarkup = Object.fromEntries(Object.entries(markup).map(([key, value]) => [key, Number(value)]))
        const dict = { name, phone, email, password, balance: Number(balance), markup: convertedMarkup, remarks, disableDiscounts }
        await axios.put('/api/reseller/' + reseller._id, dict)
            .then(() => {
                setShowSuccessMessage(true)
            })
            .catch(err => {
                console.log(`create reseller failed with error: ${err}`)
                alert(`Failure creating reseller, please try again. Error: ${err}`)
            })
    }
    const handleCancelClicked = () => {
        goBack()
    }

    const renderDetailsFields = () => {
        return (
            <Container>
                <Form inverted>
                    <FormGroup widths="equal">
                        <FormField control={Input} label="Name" name="name" value={name} placeholder="Name" onChange={handleChangeField} inverted className="dark-theme-input"/>
                        <FormField control={Input} label="Phone" name="phone" value={phone} placeholder="Phone" onChange={handleChangeField} inverted className="dark-theme-input"/>
                    </FormGroup>
                    <FormGroup widths="equal">
                        <FormField control={Input} label="Email" name="email" value={email} placeholder="" onChange={handleChangeField} inverted className="dark-theme-input"/>
                        <FormField control={Input} label="Password" type="password" name="password" value={password}  onChange={handleChangeField} inverted className="dark-theme-input"/>
                    </FormGroup>
                    <FormField control={Input} label="Balance" name="balance" value={balance} placeholder="" onChange={handleChangeField} inverted className="dark-theme-input"/>
                    <FormField control={TextArea} rows="1" label="Remarks" name="remarks" value={remarks} placeholder="" onChange={handleChangeField} inverted className="dark-theme-textarea-field"/>
                    <FormField control={Checkbox} label="Disable Discounts" name="disableDiscounts" checked={disableDiscounts} onChange={handleToggle} inverted/>
                </Form>
            </Container>
        )
    }
    const renderPriceMarkUpFields = () => {
        const fields = []
        fields.push( // mlbb fields are fixed
        <FormGroup key={'lol'} widths="equal">
            <FormField required control={Input} label="mlbb" name="markup-mlbb" value={markup.mlbb} onChange={handleChangeField} inverted className="dark-theme-input"/>
            <FormField required control={Input} label="mlbb_global" name="markup-mlbb_global" value={markup.mlbb_global} onChange={handleChangeField} inverted className="dark-theme-input"/>
            <FormField required control={Input} label="mlbb_special" name="markup-mlbb_special" value={markup.mlbb_special} onChange={handleChangeField} inverted className="dark-theme-input"/>
        </FormGroup>)

        const gameFields = [] // other games' fields are dynamic
        for (const game of launchedGames) {
            if (game.supplierName === 'mlbb') continue
            const fieldName = 'markup-' + game.supplierName
            gameFields.push(<FormField key={game.supplierName} control={Input} label={game.name} name={fieldName} value={markup[game.supplierName]} onChange={handleChangeField} inverted className="dark-theme-input"/>)
        }
        for (let i = 0; i < gameFields.length; i += 3) { // 3 fields per group
            fields.push(<FormGroup widths="equal" key={`game-group-${i}`}>{gameFields.slice(i, i + 3)}</FormGroup>)
        }
        return <Form inverted>{fields}</Form>
    }
    const renderPagination = () => {
        if (!reseller.balanceHistory) return null
        const pagesRequired = Math.ceil(reseller.balanceHistory.length / itemsPerPage)
        return <Pagination inverted defaultActivePage={1} totalPages={pagesRequired} siblingRange={2} onPageChange={handlePageChange}/>
    }

    const renderTableRows = () => {
        if (!reseller.balanceHistory) return null
        const rows = []
        for (const [i, transaction] of reseller.balanceHistory.entries()) {
            rows.push(
                <TableRow key={i}>
                    {!!transaction.date ? (<TableCell style={{ color: 'white' }}>{format(new Date(transaction.date), 'd MMM yyyy, h:mm a')}</TableCell>) : (<TableCell/>)}
                    <TableCell style={{ color: 'white' }}>{transaction.old.toFixed(2)}</TableCell>
                    <TableCell style={{ color: 'white' }}>{transaction.new.toFixed(2)}</TableCell>
                    <TableCell style={{ color: 'white' }}>
                        {stringifyTransactionType(transaction.type)}
                        {transaction.type === 'order_placed' && !!transaction.order ? (
                            <Popup inverted wide trigger={<Icon inverted circular name="info" size="small" />} position="bottom left">
                                <span style={{ color: 'white' }}>
                                    Order ID: {transaction.order.id}<br />
                                    Date: {format(new Date(transaction.order.date), 'd MMM yyyy, h:mm a')}<br />
                                    Item: {transaction.order.item}<br />
                                    Amount: ${transaction.order.amount}
                                </span>
                            </Popup>
                        ) : null}
                    </TableCell>
                </TableRow>
            )
        }
        return rows.reverse().slice(activePage * itemsPerPage, activePage * itemsPerPage + itemsPerPage)
    }
    const renderAccountBalanceHistory = () => {
        const style = window.innerWidth > 1035 ? { marginTop: 35 } : { marginTop: 35, overflowX: 'auto', display: 'block', whiteSpace: 'nowrap' }
        return (
            <div>
                <Table inverted style={style} size={window.innerWidth < 768 ? 'small' : null} celled striped unstackable>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>Date</TableHeaderCell>
                            <TableHeaderCell>Old Balance</TableHeaderCell>
                            <TableHeaderCell>New Balance</TableHeaderCell>
                            <TableHeaderCell>Type</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {renderTableRows()}
                    </TableBody>
                </Table>
                {renderPagination()}
            </div>
        )
    }

    return (
        <Container inverted style={{ background: '#1b1c1d', marginTop: 35 }}>
            <Breadcrumb inverted style={{ marginBottom: 30 }} size="big">
                <BreadcrumbSection onClick={handleCancelClicked} style={{ color: 'white', cursor: 'pointer' }}>Resellers</BreadcrumbSection>
                <BreadcrumbDivider color='teal' icon="right chevron" />
                <BreadcrumbSection active style={{ color: 'white' }}>{name}</BreadcrumbSection>
            </Breadcrumb>
            <Divider inverted style={{ marginTop: 5, marginBottom: 20 }} horizontal><Header inverted as="h4"><Icon inverted name="info" />Details</Header></Divider>
            {renderDetailsFields()}
            <Divider inverted style={{ marginTop: 55, marginBottom: 20 }} horizontal><Header inverted as="h4"><Icon inverted name="dollar" />Price Markup %</Header></Divider>
            {renderPriceMarkUpFields()}
            <Divider inverted style={{ marginTop: 55, marginBottom: 20 }} horizontal><Header inverted as="h4"><Icon inverted name="history" />Account Balance History</Header></Divider>
            {renderAccountBalanceHistory()}
            <br /><br /><br /><br /><br />
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1b1c1d', borderTop: '2px solid #ccc', padding: '10px', zIndex: 100 }}>
                {showSuccessMessage ? (
                    <Message inverted style={{ marginTop: 20 }} success header="Changes saved successfully."/>
                ) : null}
                <Container>
                    <Button inverted color='teal' style={{ marginTop: 10, marginRight: 15, marginBottom: 10 }} onClick={handleSaveClicked}><Icon inverted color='teal' name="save" />Save Changes</Button>
                    <Button inverted onClick={handleCancelClicked}>
                        <Icon inverted name="reply" />Back
                    </Button>
                </Container>
            </div>
        </Container>
    )
}

const stringifyTransactionType = type => {
    if (type === 'initial') return 'Starting Account Balance'
    if (type === 'order_placed') return 'Order placed'
    if (type === 'order_refunded') return 'Order refunded'
    if (type === 'top_up') return 'Account Top up'
    if (type === 'deduct') return 'Account Deduction'
    return type
}

export default Reseller